import React, { useEffect, useState } from "react";

/*
 * Office-network gate. Before ANY app UI (including login) renders, this checks
 * GET /api/access-check. While waiting it shows a blank screen. On 403 (outside
 * the office network) or any failure it renders ONLY the denied message with the
 * detected IP — no other component, route, or data. The result is never cached
 * (component state only, cache:"no-store"), so it re-checks on every load.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AccessGate({ children }) {
  const [status, setStatus] = useState("checking"); // "checking" | "allowed" | "denied"
  const [ip, setIp] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/access-check`, {
          method: "GET",
          cache: "no-store",
          headers: { "Cache-Control": "no-store" },
        });
        if (cancelled) return;
        if (res.ok) {
          setStatus("allowed");
          return;
        }
        // 403 (or any non-OK) → denied. Try to surface the detected IP.
        let detectedIp = null;
        try {
          const body = await res.json();
          detectedIp = body?.ip || null;
        } catch {
          /* no body / not JSON — still denied */
        }
        setIp(detectedIp);
        setStatus("denied");
      } catch {
        // Network / CORS failure → fail closed.
        if (!cancelled) setStatus("denied");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === "checking") {
    // Blank loading screen — nothing of the app is mounted yet.
    return <div style={{ minHeight: "100vh", background: "#ffffff" }} />;
  }

  if (status === "denied") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background: "#0f172a",
          color: "#e2e8f0",
          fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: 540 }}>
          <div style={{ fontSize: 52, marginBottom: 16 }} aria-hidden="true">🔒</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 12px" }}>Access denied</h1>
          <p style={{ fontSize: 15, lineHeight: 1.65, margin: 0 }}>
            This application works only on the AAMPL office network.
            If you are outside the office, connect to the office VPN and reload.
          </p>
          {ip && (
            <p style={{ marginTop: 18, fontSize: 13, opacity: 0.7 }}>
              Your detected IP:{" "}
              <code style={{ background: "rgba(255,255,255,0.08)", padding: "2px 6px", borderRadius: 4 }}>
                {ip}
              </code>
            </p>
          )}
        </div>
      </div>
    );
  }

  return children;
}
